import React from "react";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "./utils/cn";

type Variant =
  | "primary-1"
  | "secondary-1"
  | "primary-2"
  | "secondary-2"
  | "alternative-1"
  | "alternative-2"
  | "default";
interface ButtonProps extends React.ComponentProps<"button"> {
  variant: Variant;
  loading?: boolean;
  inverted?: boolean;
  asChild?: boolean;
}

export const Button = React.forwardRef(function Button(
  { asChild, loading, variant, inverted, className, ...props }: ButtonProps,
  ref: React.ForwardedRef<any>
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      {...props}
      ref={ref}
      disabled={props.disabled ?? loading}
      data-inverted={inverted}
      data-variant={variant}
      className={cn(
        "disabled:opacity-90 hover:opacity-95 rounded-[20px] uppercase font-semibold font-alternative text-base leading-6 flex items-center justify-center border-2 gap-2 bg-white",
        variant === "primary-1" &&
          "border-primary-1 text-primary-1 hover:bg-primary-1/5 data-[inverted=true]:bg-primary-1 data-[inverted=true]:text-white",
        variant === "primary-2" &&
          "border-primary-2 text-primary-2 hover:bg-primary-2/5",
        variant === "secondary-1" &&
          "border-secondary-1 text-secondary-1 hover:bg-secondary-1/5 data-[inverted=true]:bg-secondary-1 data-[inverted=true]:text-white",
        variant === "secondary-2" &&
          "border-secondary-2 text-secondary-2 hover:bg-secondary-2/5",
        variant === "alternative-1" &&
          "border-alternative-1 text-alternative-1 hover:bg-alternative-1/5",
        variant === "alternative-2" &&
          "border-alternative-2 text-alternative-2 hover:bg-alternative-2/5",
        variant === "default" &&
          "border-white text-primary-2 hover:bg-primary-2/5",
        className
      )}
      style={{
        boxShadow: "0px 13px 19px 0px rgba(0, 0, 0, 0.07)",
        ...props.style,
      }}
    >
      {loading ? (
        <div
          className={cn(
            "h-5 w-5 animate-spin rounded-full border-4 border-t-transparent",
            variant === "primary-1" && "border-primary-1",
            variant === "primary-2" && "border-primary-2",
            variant === "secondary-1" && "border-secondary-1",
            variant === "secondary-2" && "border-secondary-2",
            variant === "alternative-1" && "border-alternative-1",
            variant === "alternative-2" && "border-alternative-2",
            variant === "default" && "border-primary-2"
          )}
        ></div>
      ) : (
        props.children
      )}
    </Comp>
  );
});
